import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import AddDevices from "./AddDevice/addDevices";
import KonnectRegistration from './Integration/Konnect/RegisterKonnect';
import ManageDevices from "./ManageDevice/manageDevice";
import PolicyGroups from "./DeviceGroups/deviceGroups";
import Announcements from "./Announcements/announcements";
import ManageUsers from "./ManageUsers/manageUsers";
import Sidebar from "js/UserBoard/sidebar";
import Topbar from 'js/Component/topBar';
import EventCalendar from "js/UserBoard/EventCalendar/EventCalendar";
import Features from 'js/UserBoard/Features/Features';
import FormContainer from "js/UserBoard/Forms/Form";
import OrgAddressBooks from "js/UserBoard/OrgAddressBook/OrgAddressBooks";
import { fetchOrganization, fetchDevice, logout, CompleteTutorial } from 'js/Api/index';
import "css/jac.css";
import "css/bootstrap.min.css";
import "style.css";
import "css/responsive.css";
import "css/color_2.css";
import "css/bootstrap-select.css";
import "css/perfect-scrollbar.css";
import "css/custom.css";
import "css/jquery.fancybox.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "react-datepicker/dist/react-datepicker.css";
import MyProfile from "js/Component/MyProfile/myProfile";
import 'intro.js/introjs.css';
import introJs from 'intro.js';

function Userboard() {
    const [activeComponent, setActiveComponent] = useState("Manage Devices");
    const [userInfo, setUserInfo] = useState(null);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [initialTab, setInitialTab] = useState(null);
    const [resetFlag, setResetFlag] = useState(false);
    const isSuperAdmin = userInfo?.role === "SuperAdmin";
    const isAdmin = userInfo?.role === "Admin";
    const isStaff = userInfo?.role === "staff";
    const isManager = userInfo?.role === "manager";
    const { orgGuid } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [ShowTutorialUpdate, setShowTutorialUpdate] = useState(false);
    const [isLoading, setIsLoading] = useState(true); // Loading state

    // Parse URL parameters
    useEffect(() => {

        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';

        if (!isLoggedIn) {
            const currentUrl = window.location.href;
            navigate(`/?redirect=${encodeURIComponent(currentUrl)}`);
            return;
        }

        const queryParams = new URLSearchParams(location.search);
        const deviceId = queryParams.get("device_id");
        const tabName = queryParams.get("tab");

        if (deviceId) {
            setActiveComponent("Manage Devices");
            if (tabName) {
                setInitialTab(tabName);
            }
        }
    }, [location, navigate]);

    useEffect(() => {
        const storedUserInfo = localStorage.getItem("userInfo");
        if (storedUserInfo) {
            try {
                const parsedUserInfo = JSON.parse(storedUserInfo);
                if (parsedUserInfo?.isTutorialComplete === true && parsedUserInfo?.orgType === "family") {
                    setShowTutorialUpdate(true)
                }
                setUserInfo(parsedUserInfo);
            } catch (error) {
                console.error("Error parsing userInfo:", error);

                localStorage.removeItem("userInfo");
            }
        }
    }, []);

    useEffect(() => {
        if (isStaff || isManager) {
            if (userInfo?.organizationGuid) {
                getOrganizationData(userInfo?.organizationGuid);
            }
        } else if ((isAdmin || isSuperAdmin) && orgGuid) {
            getOrganizationData(orgGuid);
        }
    }, [userInfo, orgGuid, isStaff, isManager, isAdmin, isSuperAdmin]);

    const getOrganizationData = async (guid) => {
        const result = await fetchOrganization(guid);

        if (result.success) {
            setSelectedOrg(result.data);

            // After we have org data, check if we need to open a specific device
            const queryParams = new URLSearchParams(location.search);
            const deviceId = queryParams.get("device_id");
            if (deviceId) {
                await getDeviceData(deviceId);
            }
            else {
                let hashes = location.hash.split("#");
                if (hashes.length > 1 && hashes[1] === "device_groups") {
                    setActiveComponent("Policy Groups");
                }
            }
            setIsLoading(false);
        } else {
            console.log("Failed to fetch organization.");
        }
    };

    const getDeviceData = async (deviceId) => {
        const result = await fetchDevice(deviceId);

        if (result.success) {
            setSelectedUser(result.data);
        } else {
            console.log("Failed to fetch device.");
            toast.error("Device not found or you don't have permission to access it.");
        }
    };

    const handleSetActiveComponent = (componentName) => {
        setActiveComponent(componentName);

        setResetFlag((prev) => !prev);
    };

    const handleProfileBackClick = () => {
        navigate(location.pathname + location.search, { replace: true });
    };

    const handleLogout = async () => {
        try {
            const result = await logout();

            if (result.success) {
                localStorage.clear();
                navigate("/");
            } else {
                console.log("Logout failed:", result.error);
            }
        } catch (error) {
            console.log("Error:", error);
        }
    };

    const OnTutorialUpdate = async () => {
        StartTour();
    }

    const renderComponent = () => {
        switch (activeComponent) {
            case "Register Konnekt":
                return (
                    <KonnectRegistration />
                );
            case "Add Devices":
                return (
                    <AddDevices setActiveComponent={handleSetActiveComponent} org={selectedOrg} resetFlag={resetFlag} />
                );
            case "Manage Devices":
                return (
                    <ManageDevices settingsFrom="ManageDevices" selectedOrg={selectedOrg} userInfo={userInfo} resetFlag={resetFlag}
                        initialSelectedUser={selectedUser} initialTab={initialTab} />
                );
            case "Policy Groups":
                return (
                    <PolicyGroups settingsFrom="PolicyGroups" org={selectedOrg} resetFlag={resetFlag} userInfo={userInfo} />
                );
            case "Announcements":
                return <Announcements org={selectedOrg} resetFlag={resetFlag} />;
            case "Event Calendar":
                return <EventCalendar userInfo={userInfo} org={selectedOrg} />;
            case "Store":
                return <Features userInfo={userInfo} org={selectedOrg} resetFlag={resetFlag} />;
            case "Manage Users":
                return (
                    <ManageUsers userInfo={userInfo} selectedOrg={selectedOrg} orgGuid={orgGuid} resetFlag={resetFlag} />
                );
            case "Forms":
                return <FormContainer org={selectedOrg} />;
            case "Address Books":
                return <OrgAddressBooks org={selectedOrg} />;
            default:
                return null;
        }
    };

    useEffect(() => {
        if (!isLoading && !selectedUser && userInfo?.isTutorialComplete !== true && userInfo?.orgType === "family") {
            StartTour();
        }
    }, [isLoading, selectedUser]);

    const StartTour = () => {
        const timer = setTimeout(() => {
            const tour = introJs();

            let tutorial_steps = [
                {
                    intro: 'Welcome to the CareWindow Admin Portal! <br /><br />This short tutorial will introduce the key features.',
                },
                {
                    element: '#menu_adddevice',
                    intro: "First, you'll need to add your device to the Admin Portal. <br /><br />To do this, follow the simple instructions on this page.",
                    position: 'right',
                    scrollTo: 'element',
                },
                {
                    element: '#menu_managedevice',
                    intro: 'CareWindow devices that you add will appear here. <br /><br />You can invite family members, make calls, change settings and much more.',
                    position: 'right',
                    scrollTo: 'element',
                },
                {
                    element: '#menu_announcements',
                    intro: "Send text messages to your loved one's CareWindow from this page.",
                    position: 'right',
                    scrollTo: 'element',
                }]

            if (userInfo?.role === "manager") {
                tutorial_steps.push({
                    element: '#menu_manageusers',
                    intro: 'Invite other family members to the Admin Portal from this page.',
                    position: 'right',
                    scrollTo: 'element',
                })
            }

            tour.setOptions({
                steps: tutorial_steps,
                showStepNumbers: true,
                showProgress: false,
                showBullets: false,
                hidePrev: true,
                skipLabel: 'Skip',
                doneLabel: 'Finish'
            });

            tour.onafterchange(function (targetElement) {
                setTimeout(() => {

                    // Extract current step from Intro.js default red badge which evaluates reliably
                    const defaultBadge = document.querySelector('.introjs-helperNumberLayer');
                    let currentStep = defaultBadge ? defaultBadge.textContent.trim() : "1";
                    currentStep = currentStep.replace((" of " + tour._options.steps.length.toString()), "");

                    if (currentStep === tour._options.steps.length.toString()) {

                    }
                    else {
                        const skipBtn = document.querySelector('.introjs-skipbutton');
                        const btnContainer = document.querySelector('.introjs-tooltipbuttons');
                        if (skipBtn && btnContainer && skipBtn.parentElement !== btnContainer) {
                            if (skipBtn.innerHTML.includes('&times;') || skipBtn.innerHTML === '×') {
                                skipBtn.innerHTML = 'Skip';
                            }
                            btnContainer.appendChild(skipBtn);
                        }
                    }

                    const tooltip = document.querySelector('.introjs-tooltip');
                    if (tooltip) {
                        let stepCounter = tooltip.querySelector('.custom-step-counter');
                        if (!stepCounter) {
                            stepCounter = document.createElement('div');
                            stepCounter.className = 'custom-step-counter';
                            tooltip.insertBefore(stepCounter, tooltip.firstChild);
                        }

                        // If tour._options fails, fallback to hardcoded 5 steps length defined above
                        const totalSteps = (tour._options && tour._options.steps) ? tour._options.steps.length : 5;
                        stepCounter.innerHTML = `${currentStep}/${totalSteps}`;
                    }
                }, 0);
            });

            let isSkipped = false;
            tour.onskip(() => {
                isSkipped = true;
            });

            // Triggered when the user clicks 'Finish' (completes the last step)
            tour.oncomplete(async () => {
                if (isSkipped) return;
                try {
                    await CompleteTutorial(userInfo.userId);
                    userInfo.isTutorialComplete = true;
                    localStorage.setItem("userInfo", JSON.stringify(userInfo));
                    setShowTutorialUpdate(true)
                } catch (error) {

                }
            });

            tour.start();
        }, 1000);
        return () => clearTimeout(timer);
    }

    return (
        <div className="dashboard dashboard_2">
            <Toaster />
            <div className="full_container">
                <div className="inner_container">
                    <Sidebar org={selectedOrg} setActiveComponent={handleSetActiveComponent} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} isManager={isManager} />
                    <div id="content" style={{}}>
                        <Topbar userInfo={userInfo} org={selectedOrg} onLogout={handleLogout} OnTutorialUpdate={OnTutorialUpdate} ShowTutorialUpdate={ShowTutorialUpdate} />
                        <div className="content">
                            <div className="midde_cont">
                                <div className="container-fluid">
                                    {
                                        location.hash === "#profile" ? <MyProfile onBackClick={handleProfileBackClick} /> :
                                            (location.hash === "#forms" || location.hash.startsWith("#forms&")) ? <FormContainer org={selectedOrg} /> :
                                                renderComponent()
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Userboard;